export type UserRole = 'merchant' | 'bank';

/** Default merchant ID used when the role is 'merchant' and no explicit ID is set. */
export const DEFAULT_MERCHANT_ID = 'merch_001';

interface SessionData {
  email: string;
  isAuthenticated: boolean;
  loginTime: number;
  role: UserRole;
}

/**
 * Simplified session service using localStorage only.
 * Removes sessionId validation and beforeunload handlers that caused
 * session-wipe bugs during Vite HMR.
 */
class SessionService {
  private readonly SESSION_KEY = 'auth-session';
  private readonly ROLE_KEY = 'user-role';
  private readonly MERCHANT_ID_KEY = 'merchant-id';
  private readonly SESSION_DURATION_MS = 8 * 60 * 60 * 1000; // 8 hours

  saveSession(email: string, _password: string): void {
    const role = this.getRole();
    const sessionData: SessionData = {
      email,
      isAuthenticated: true,
      loginTime: Date.now(),
      role,
    };
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
  }

  getSession(): SessionData | null {
    try {
      const sessionStr = localStorage.getItem(this.SESSION_KEY);
      if (!sessionStr) return null;
      const session = JSON.parse(sessionStr) as SessionData;
      if (!session.isAuthenticated) return null;
      // Expire session after SESSION_DURATION_MS (8 hours)
      if (Date.now() - session.loginTime > this.SESSION_DURATION_MS) {
        this.logout();
        return null;
      }
      return session;
    } catch {
      return null;
    }
  }

  logout(): void {
    localStorage.removeItem(this.SESSION_KEY);
  }

  /** Role helpers — persisted independently so the login selector can set it before auth */
  getRole(): UserRole {
    return (localStorage.getItem(this.ROLE_KEY) as UserRole) || 'merchant';
  }

  setRole(role: UserRole): void {
    localStorage.setItem(this.ROLE_KEY, role);
    // Also update the live session if one exists
    const session = this.getSession();
    if (session) {
      session.role = role;
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    }
  }

  /** Merchant ID helpers — only meaningful when role === 'merchant' */
  getMerchantId(): string | null {
    if (this.getRole() !== 'merchant') return null;
    return localStorage.getItem(this.MERCHANT_ID_KEY) || DEFAULT_MERCHANT_ID;
  }

  setMerchantId(merchantId: string): void {
    localStorage.setItem(this.MERCHANT_ID_KEY, merchantId);
  }
}

export const sessionService = new SessionService();
